<script setup lang="ts">
import config from "~/config.json";
import arrayChunks from "~/utils/arrayChunks";
import {ref, computed} from "vue";
import UiPagination from "~/components/ui/Pagination.vue";

const props = defineProps({
  maxRowsPerPage: {
    type: Number,
    default: 1,
  },
  pagination: {
    type: Boolean,
    default: true,
  },
  enableOpenImage: {
    type: Boolean,
    default: true,
  },
});

const projectsGalleryItems = computed<{ image: string, date: string }[][][]>(() => {
  const items = arrayChunks(config["projects-gallery"], 2) || [];

  return arrayChunks(items, props.maxRowsPerPage || 1) || [];
});
const totalPages = computed<number>(() => {
  return projectsGalleryItems.value.length;
});
const currentPage = ref<number>(0);
const showBigImg = ref<boolean>(false);
const bigImage = ref<string | null>(null);

const openImage = (image: string): void => {
  if (!props.enableOpenImage) return;

  showBigImg.value = true;
  bigImage.value = image;
  document.body.classList.add("no-scroll");
};

const closeImage = (): void => {
  if (!props.enableOpenImage) return;

  showBigImg.value = false;
  bigImage.value = null;
  document.body.classList.remove("no-scroll");
};
</script>

<template>
  <div class="big-img" v-if="showBigImg">
    <img :src="`/images/projects/${bigImage}`" alt="Obrázek galerie" loading="lazy" quality="100" />

    <div class="icon-box">
      <Icon class="icon" name="material-symbols:close-rounded" @click="closeImage" />
    </div>
  </div>

  <div class="gallery">
    <div class="row" v-for="(row, rowIndex) in projectsGalleryItems[currentPage]" :key="rowIndex" :class="{ 'reverse': rowIndex % 2 === 0 }">
      <div class="img-box" v-for="(item, itemIndex) in row" :key="itemIndex" :class="{ 'large': itemIndex % 2 === 0 }" @click="openImage(item.image)">
        <img quality="70" :src="`/images/projects/${item.image}`" alt="Obrázek galerie" loading="lazy" />

        <Icon name="material-symbols:pan-zoom-rounded" class="icon" />

        <span class="date">30. 08. 2025</span>
      </div>
    </div>

    <UiPagination v-model="currentPage" :totalPages="totalPages" v-if="pagination" />
  </div>
</template>

<style scoped lang="scss">
.big-img {
  position: fixed;
  inset: 0;
  background: rgba(var(--dark-background), 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  .icon-box {
    position: absolute;
    top: 30px;
    right: 30px;
    font-size: 40px;
    padding: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(var(--light-background), 0.8);
    border-radius: var(--normal-border-radius);
    cursor: pointer;
    transition: 0.2s;

    .icon {
      color: rgb(var(--text-light-color));
      transition: 0.2s;
      line-height: 0;
    }

    &:hover .icon {
      color: rgb(var(--title-light-color));
    }
  }
}

.gallery {
  display: flex;
  flex-direction: column;
  gap: 30px;

  .row {
    display: flex;
    width: 100%;
    gap: 30px;
    flex-wrap: wrap;
    flex-direction: row-reverse;

    &.reverse {
      flex-direction: row;
    }

    .img-box {
      position: relative;
      border-radius: var(--large-border-radius);
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      min-height: 200px;
      min-width: 250px;
      aspect-ratio: 1;
      max-height: 400px;
      transition: 0.2s;
      cursor: pointer;
      flex-wrap: wrap;
      flex: 1;

      &::after {
        content: "";
        position: absolute;
        inset: 0;
        background: rgba(var(--dark-background), 0);
        transition: 0.2s;
        z-index: 1;
        pointer-events: none;
      }

      &.large {
        flex: 2;
      }

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        position: absolute;
        top: 0;
        left: 0;
        transition: 0.2s;
        z-index: 0;
      }

      .icon {
        position: absolute;
        font-size: 50px;
        color: rgb(var(--title-dark-color));
        z-index: 2;
        opacity: 0;
        transition: 0.2s;
      }

      .date {
        transition: 0.2s;
        position: absolute;
        bottom: 15px;
        right: 15px;
        background: rgba(var(--dark-background), 0.8);
        color: rgb(var(--mini-title-dark-color));
        padding: 10px 20px;
        border-radius: var(--small-border-radius);
        font-size: 14px;
        z-index: 2;
      }

      &:hover {
        &::after {
          background: rgba(var(--dark-background), 0.8);
        }

        img {
          transform: scale(1.03);
        }

        .icon {
          opacity: 1;
        }

        .date {
          background: rgb(var(--light-background), 0.2);
        }
      }
    }
  }
}
</style>